import React from 'react';
import { motion, useInView } from 'framer-motion';

const ListenAnywhere = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const platforms = [
    {
      name: 'Spotify',
      color: '#1DB954',
      href: 'https://open.spotify.com/show/1tuj8UXzDHXLNFsdZGeS8O?si=b237f74394b64200',
      logo: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
      )
    },
    {
      name: 'Apple Podcasts',
      color: '#A855F7',
      href: 'https://podcasts.apple.com/us/podcast/boss-at-heart-from-pain-to-purpose/id1517815773',
      logo: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      )
    },
    {
      name: 'YouTube',
      color: '#FF0000',
      href: 'https://www.youtube.com/@camgocrazy_bossatheart/featured',
      logo: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-full h-full">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      )
    }
  ];

  return (
    <section
      id="listen-anywhere"
      className="section py-16 sm:py-20 md:py-24 relative"
      style={{ backgroundColor: 'var(--light-gray)' }}
      ref={ref}
    >
      {/* Waveform Background */}
      <div className="absolute inset-0 opacity-5">
        <svg viewBox="0 0 1200 400" className="w-full h-full">
          <path d="M0,200 Q300,100 600,200 T1200,200" stroke="#E3242B" strokeWidth="4" fill="none" />
          <path d="M0,250 Q300,150 600,250 T1200,250" stroke="#E3242B" strokeWidth="3" fill="none" />
          <path d="M0,150 Q300,50 600,150 T1200,150" stroke="#E3242B" strokeWidth="2" fill="none" />
        </svg>
      </div>

      <div className="container relative z-10 px-4">
        <motion.div
          className="text-center max-w-4xl mx-auto"
          initial={{ y: 50, opacity: 0 }}
          animate={isInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-gray-900">
            Stream It Wherever You Hustle.
          </h2>

          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-12 leading-relaxed px-2">
            Whether you're on the road, in the gym, or chasing your next move — the message follows you.
          </p>

          {/* Platform Badges */}
          <motion.div
            className="flex flex-wrap justify-center gap-6 sm:gap-8 mb-12 sm:mb-16"
            initial={{ y: 30, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {platforms.map((platform, index) => (
              <a
                key={platform.name}
                href={platform.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <motion.div
                  className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-gray-200"
                  whileHover={{ scale: 1.08, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ y: 30, opacity: 0 }}
                  animate={isInView ? { y: 0, opacity: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                >
                  <div className="relative mb-4">
                    <div
                      className="w-16 h-16 sm:w-20 sm:h-20 mx-auto rounded-2xl shadow-lg flex items-center justify-center text-white transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                      style={{
                        backgroundColor: platform.color,
                        background: `linear-gradient(135deg, ${platform.color} 0%, ${platform.color}dd 100%)`
                      }}
                    >
                      <div className="w-8 h-8 sm:w-10 sm:h-10">
                        {platform.logo}
                      </div>
                    </div>
                    {/* Glow effect on hover */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl"
                      style={{ backgroundColor: platform.color }}
                    ></div>
                  </div>
                  <p className="font-bold text-gray-900 text-base sm:text-lg mb-1">{platform.name}</p>
                  <p className="text-xs sm:text-sm text-gray-500 font-medium">Listen Now</p>
                </motion.div>
              </a>
            ))}
          </motion.div>

          {/* Spotify Radio Embed */}
          <motion.div
            className="mb-12 sm:mb-16"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isInView ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-3xl p-8 shadow-2xl max-w-md mx-auto border border-green-200 relative overflow-hidden">
              {/* Decorative pattern */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-green-300 rounded-full -ml-12 -mb-12 opacity-20"></div>

              <div className="relative z-10">
                <div className="flex items-center mb-6">
                  <div className="w-4 h-4 bg-green-500 rounded-full mr-3 animate-pulse shadow-lg"></div>
                  <span className="text-sm font-bold text-green-700 uppercase tracking-wider">Live Radio</span>
                </div>

                <iframe
                  data-testid="embed-iframe"
                  style={{ borderRadius: '12px' }}
                  src="https://open.spotify.com/embed/show/1tuj8UXzDHXLNFsdZGeS8O?utm_source=generator"
                  width="100%"
                  height="152"
                  frameBorder="0"
                  allowfullscreen=""
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  className="w-full shadow-inner"
                />

                <div className="mt-4 text-sm text-green-600 font-bold flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                  </svg>
                  Now Playing: Boss At Heart Podcast
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tagline */}
          <motion.p
            className="text-base sm:text-lg text-gray-600 italic font-medium"
            initial={{ y: 30, opacity: 0 }}
            animate={isInView ? { y: 0, opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            "Because healing and hustle belong in the same sentence."
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default ListenAnywhere;